import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import LandingNavbar from '../components/LandingNavbar';
import LandingFooter from '../components/LandingFooter';
import Hero from '../components/Hero';
import Steps from '../components/Steps';
import FAQ from '../components/FAQ';
import Testimonials from '../components/Testimonials';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Shield, Clock, Award, ArrowRight, Star, Users, TrendingUp, Zap, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { packages } from '../data/mock';
import RegistrationModal from '../components/RegistrationModal';

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Helmet>
        <title>Trademark Registration - Fast & Affordable from $45 | Global Trademark Pro</title>
        <meta name="description" content="Register your trademark today for only $45. Fast, professional USPTO trademark filing with expert guidance and high success rates. Protect your brand now!" />
        <meta name="keywords" content="trademark registration, register trademark, USPTO filing, affordable trademark, trademark attorney, brand protection" />
        <link rel="canonical" href="https://yourdomain.com/lp" />
      </Helmet>
      <LandingNavbar />
      
      <main className="flex-1">
        <Hero />
        
        {/* Simplified Value Proposition */}
        <section className="py-12 bg-white border-y border-gray-200">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">100% Secure</h3>
                <p className="text-gray-600 text-sm">Protected & encrypted</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Fast Processing</h3>
                <p className="text-gray-600 text-sm">File in minutes</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Award className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Expert Support</h3>
                <p className="text-gray-600 text-sm">Professional guidance</p>
              </div>
            </div>
          </div>
        </section>

        {/* NEW SECTION 1: Success Stats with Creative Design */}
        <section className="py-16 bg-gradient-to-br from-emerald-600 via-emerald-500 to-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold">Trusted by Thousands</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Why Businesses Choose Us
              </h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">15K+</div>
                <p className="text-white/90 text-sm">Trademarks Filed</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">98%</div>
                <p className="text-white/90 text-sm">Success Rate</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">24/7</div>
                <p className="text-white/90 text-sm">Expert Support</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">10+</div>
                <p className="text-white/90 text-sm">Years Experience</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Compact Pricing */}
        <section id="pricing" className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Simple, Transparent Pricing
              </h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Choose the package that best fits your needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {packages.trademark.map((pkg, index) => (
                <Card key={index} className={`relative transition-all duration-300 hover:shadow-xl flex flex-col ${pkg.featured ? 'border-2 border-emerald-500 shadow-lg scale-105' : 'hover:border-emerald-200'}`}>
                  {pkg.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-3 py-1 rounded-full text-xl font-semibold whitespace-nowrap z-10">
                      Most Popular
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{pkg.name}</CardTitle>
                    <CardDescription className="text-2xl font-bold text-gray-900">
                      ${pkg.price}<span className="text-xl text-gray-500">*</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pb-2">
                    <div className="max-h-[180px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                      <ul className="space-y-1.5">
                        {pkg.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-base text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xl text-gray-500 mt-2">*Govt. fee not included</p>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Button onClick={() => setIsModalOpen(true)} className={`w-full h-9 text-xl ${pkg.featured ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' : 'bg-gray-900 hover:bg-gray-800'}`}>
                      Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* NEW SECTION 2: Benefits with Creative Layout */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  Everything You Need to Protect Your Brand
                </h2>
                <p className="text-base text-gray-600">
                  Complete trademark protection made simple
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="group relative bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Lightning Fast Filing</h3>
                  <p className="text-base text-gray-600">Submit your trademark application in minutes, not hours. Our streamlined process makes it quick and easy.</p>
                </div>
                <div className="group relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Dedicated Support Team</h3>
                  <p className="text-base text-gray-600">Get personalized assistance from trademark experts who guide you through every step of the process.</p>
                </div>
                <div className="group relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Proven Track Record</h3>
                  <p className="text-base text-gray-600">Join 15,000+ successful trademark registrations with our 98% approval rate and decade of expertise.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Steps />
        <FAQ />
        <Testimonials />
        
        {/* Enhanced Final CTA */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Star className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-semibold">Limited Time Offer</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Protect Your Brand Today
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of successful businesses. Start your trademark registration now and secure your brand's future.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  size="lg" 
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white h-14 px-10 text-lg font-semibold shadow-2xl hover:shadow-emerald-500/50 transition-all"
                >
                  Start Your Registration Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  size="lg" 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white/20 h-14 px-10 text-lg font-semibold"
                >
                  Get Free Consultation
                </Button>
              </div>
              <div className="flex items-center justify-center gap-8 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>No Hidden Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>Money-Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <LandingFooter />
      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default LandingPage;